import { ActiveTemplate, CanvasElement, ColumnDefinition, TableRow } from '../types';

export interface IndustryCategory {
  name: string;
  group: 'Retail & Commerce' | 'Food & Hospitality' | 'Medical & Wellness' | 'Professional Services' | 'Logistics & Industrial' | 'Tech, Design & Academic';
  primaryColor: string;
  textColor: string;
  senderName: string;
  senderAddr: string;
  recipientLabel: string;
  recipientName: string;
  recipientAddr: string;
  billTitle: string;
  columns: ColumnDefinition[];
  items: { description: string; qty: number; rate: number; sku: string }[];
  stampText: string;
  stampShape: 'circle' | 'oval' | 'rectangle' | 'badge' | 'pill';
  terms: string;
}

export const INDUSTRY_CATEGORIES: IndustryCategory[] = [
  // ==================== 1. RETAIL & COMMERCE (30) ====================
  {
    name: "Grocery Supermarket",
    group: "Retail & Commerce",
    primaryColor: "#16a34a",
    textColor: "#15803d",
    senderName: "DAILY HARVEST SUPERMARKET",
    senderAddr: "900 Fresh Produce Lane, Seattle, WA 98101\nbilling@dailyharvest.com",
    recipientLabel: "RECIPIENT / PATRON",
    recipientName: "Retail Delivery Account #405",
    recipientAddr: "400 Pine St, Suite 12B\nSeattle, WA 98101",
    billTitle: "RETAIL SALES SLIP",
    columns: [
      { id: "c1", header: "Item Barcode / Description", width: 50, align: "left", key: "description" },
      { id: "c2", header: "Qty", width: 12, align: "center", key: "qty" },
      { id: "c3", header: "Unit Price", width: 18, align: "right", key: "rate" },
      { id: "c4", header: "Total Net", width: 20, align: "right", key: "total" }
    ],
    items: [
      { description: "Organic Honeycrisp Apples [S/N: 70412]", qty: 8, rate: 3.49, sku: "SKU-HCA-08" },
      { description: "Grass-Fed Ribeye Steak (USDA Choice)", qty: 2, rate: 19.99, sku: "SKU-RBY-02" },
      { description: "Cold-Pressed Artisan Olive Oil 750ml", qty: 1, rate: 24.50, sku: "SKU-AOO-01" }
    ],
    stampText: "ITEMS DELIVERED",
    stampShape: "rectangle",
    terms: "Thank you for shopping at Daily Harvest! Freshness is guaranteed. Standard grocery sales tax applied."
  },
  {
    name: "Fashion Apparel Boutique",
    group: "Retail & Commerce",
    primaryColor: "#db2777",
    textColor: "#be185d",
    senderName: "VELVET HOUR ATELIER",
    senderAddr: "10 Madison Ave, Floor 2, New York, NY 10010\nconcierge@velvethour.com",
    recipientLabel: "CLIENT DIRECT",
    recipientName: "Regina George",
    recipientAddr: "89 Beverly Hills Boulevard\nLos Angeles, CA 90210",
    billTitle: "COUTURE ACCOUNT STATEMENT",
    columns: [
      { id: "col1", header: "Design Style / Accessories", width: 50, align: "left", key: "description" },
      { id: "col2", header: "Qty", width: 12, align: "center", key: "qty" },
      { id: "col3", header: "Rate", width: 18, align: "right", key: "rate" },
      { id: "col4", header: "Amount", width: 20, align: "right", key: "total" }
    ],
    items: [
      { description: "Velvet Hour Bias-Cut Silk Slip Dress (Emerald)", qty: 1, rate: 380.00, sku: "SKU-VSS-01" },
      { description: "Guise Handcrafted Brass Hoops", qty: 1, rate: 110.00, sku: "SKU-GBH-01" },
      { description: "Tailored Linen Blazer (Pearl Sand)", qty: 1, rate: 295.00, sku: "SKU-TLB-01" }
    ],
    stampText: "FINAL SALE",
    stampShape: "badge",
    terms: "Return policy: Unworn items with tags can be exchanged for salon credit within 14 days."
  },
  {
    name: "Independent Bookstore",
    group: "Retail & Commerce",
    primaryColor: "#854d0e",
    textColor: "#713f12",
    senderName: "PAGES & PARCHMENT BOOKS",
    senderAddr: "404 Poet's Alley, Boston, MA 02108\nhello@pagesparchment.com",
    recipientLabel: "ACADEMIC SUBSCRIBER",
    recipientName: "Prof. Arthur Pendelton (Dept of English)",
    recipientAddr: "Harvard Library Annex Room 4\nCambridge, MA 02138",
    billTitle: "BOOKSTORE BILLINGS",
    columns: [
      { id: "b1", header: "ISBN & Title / Author", width: 55, align: "left", key: "description" },
      { id: "b2", header: "Qty", width: 10, align: "center", key: "qty" },
      { id: "b3", header: "Cover List", width: 15, align: "right", key: "rate" },
      { id: "b4", header: "Total", width: 20, align: "right", key: "total" }
    ],
    items: [
      { description: "The Odyssey (Fagles Translation, Hardcover)", qty: 1, rate: 45.00, sku: "SKU-ODY-01" },
      { description: "Anatomy of Melancholy Vol. I - III Suite", qty: 1, rate: 120.00, sku: "SKU-AMY-V3" },
      { description: "Modern literary Theory & Hermeneutics Bundle", qty: 3, rate: 29.99, sku: "SKU-MLT-BDL" }
    ],
    stampText: "BOUGHT",
    stampShape: "circle",
    terms: "Thanks for defending independent physical letters! Support your local public libraries."
  },
  // We'll generate a complete array containing 180 actual categories
  // To represent them fully, we write a programmatic lookup or build a large structural bank.
  // We'll define all remaining names and lookups inside. Let's make sure we have exactly 180 names!
];

// Complete list of 180 Category Names divided into the 6 groups
export const INDUSTRY_CATEGORIES_METADATA: { name: string; group: IndustryCategory['group']; primaryColor: string; iconKeyword: string }[] = [
  // --- Group 1: Retail & Commerce (30) ---
  { name: "Computer & Electronics Store", group: "Retail & Commerce", primaryColor: "#1e3a8a", iconKeyword: "cpu" },
  { name: "Grocery Supermarket", group: "Retail & Commerce", primaryColor: "#16a34a", iconKeyword: "shopping-cart" },
  { name: "Fashion Apparel Boutique", group: "Retail & Commerce", primaryColor: "#db2777", iconKeyword: "shirt" },
  { name: "Independent Bookstore", group: "Retail & Commerce", primaryColor: "#854d0e", iconKeyword: "book-open" },
  { name: "Artisanal Florist", group: "Retail & Commerce", primaryColor: "#22c55e", iconKeyword: "framer" },
  { name: "Luxury Jewelry Boutique", group: "Retail & Commerce", primaryColor: "#0d9488", iconKeyword: "gem" },
  { name: "Leather Footwear Shop", group: "Retail & Commerce", primaryColor: "#7c2d12", iconKeyword: "footprints" },
  { name: "Children's Toy Shop", group: "Retail & Commerce", primaryColor: "#eab308", iconKeyword: "toy" },
  { name: "Local Electronic Outlet", group: "Retail & Commerce", primaryColor: "#3b82f6", iconKeyword: "cpu" },
  { name: "Craft Bakery Shop", group: "Retail & Commerce", primaryColor: "#d97706", iconKeyword: "cookies" },
  { name: "Butcher Meat Market", group: "Retail & Commerce", primaryColor: "#ef4444", iconKeyword: "beef" },
  { name: "Fresh Catch Fishmonger", group: "Retail & Commerce", primaryColor: "#0284c7", iconKeyword: "fish" },
  { name: "Wine & Spirits Cellar", group: "Retail & Commerce", primaryColor: "#7f1d1d", iconKeyword: "glass-water" },
  { name: "Apothecary & Pharmacy", group: "Retail & Commerce", primaryColor: "#059669", iconKeyword: "pill" },
  { name: "Pet Supplies Depot", group: "Retail & Commerce", primaryColor: "#ca8a04", iconKeyword: "dog" },
  { name: "Custom Bicycle Studio", group: "Retail & Commerce", primaryColor: "#4f46e5", iconKeyword: "bike" },
  { name: "Designer Furniture Showroom", group: "Retail & Commerce", primaryColor: "#475569", iconKeyword: "sofa" },
  { name: "Neighborhood Hardware", group: "Retail & Commerce", primaryColor: "#374151", iconKeyword: "wrench" },
  { name: "Musical Instrument Shop", group: "Retail & Commerce", primaryColor: "#9333ea", iconKeyword: "music" },
  { name: "Vintage Antique Gallery", group: "Retail & Commerce", primaryColor: "#78350f", iconKeyword: "history" },
  { name: "Cosmetics Beauty Lounge", group: "Retail & Commerce", primaryColor: "#ec4899", iconKeyword: "sparkles" },
  { name: "Botanical Garden Nursery", group: "Retail & Commerce", primaryColor: "#15803d", iconKeyword: "leaf" },
  { name: "Local Souvenir Shop", group: "Retail & Commerce", primaryColor: "#f97316", iconKeyword: "gift" },
  { name: "Stationery & Planning Depot", group: "Retail & Commerce", primaryColor: "#1e3a8a", iconKeyword: "pen-tool" },
  { name: "Optical Glasses Lounge", group: "Retail & Commerce", primaryColor: "#0891b2", iconKeyword: "eye" },
  { name: "Artisanal Perfumery", group: "Retail & Commerce", primaryColor: "#c026d3", iconKeyword: "wind" },
  { name: "Watchmaker & Repair Atelier", group: "Retail & Commerce", primaryColor: "#2563eb", iconKeyword: "clock" },
  { name: "Retro Comic Book Depot", group: "Retail & Commerce", primaryColor: "#dc2626", iconKeyword: "swatchbook" },
  { name: "Boardgame Social Café", group: "Retail & Commerce", primaryColor: "#14b8a6", iconKeyword: "dice-five" },
  { name: "Fine Art Supply Annex", group: "Retail & Commerce", primaryColor: "#ea580c", iconKeyword: "palette" },
  { name: "Vintage Consignment Wardrobe", group: "Retail & Commerce", primaryColor: "#b45309", iconKeyword: "hanger" },

  // --- Group 2: Food & Hospitality (30) ---
  { name: "Artisan Coffee Roasters", group: "Food & Hospitality", primaryColor: "#7c2d12", iconKeyword: "cup" },
  { name: "Fine Dining French Bistro", group: "Food & Hospitality", primaryColor: "#1e1b4b", iconKeyword: "utensils" },
  { name: "Gourmet Burger Joint", group: "Food & Hospitality", primaryColor: "#ea580c", iconKeyword: "sandwich" },
  { name: "Japanese Sushi Lounge", group: "Food & Hospitality", primaryColor: "#991b1b", iconKeyword: "shrine" },
  { name: "Neapolitan Woodfired Pizzeria", group: "Food & Hospitality", primaryColor: "#b91c1c", iconKeyword: "pizza" },
  { name: "Organic Juice & Smoothie Bar", group: "Food & Hospitality", primaryColor: "#16a34a", iconKeyword: "glass" },
  { name: "Gelato & Ice Cream Parlor", group: "Food & Hospitality", primaryColor: "#db2777", iconKeyword: "icecream" },
  { name: "Street Food Truck catering", group: "Food & Hospitality", primaryColor: "#d97706", iconKeyword: "truck" },
  { name: "Premium Banquets Catering", group: "Food & Hospitality", primaryColor: "#0d9488", iconKeyword: "concierge" },
  { name: "Neighborhood Microbrewery", group: "Food & Hospitality", primaryColor: "#b45309", iconKeyword: "beer" },
  { name: "Speakeasy Cocktail Lounge", group: "Food & Hospitality", primaryColor: "#581c87", iconKeyword: "glass" },
  { name: "Historic Boutique Hotel", group: "Food & Hospitality", primaryColor: "#1e3a8a", iconKeyword: "hotel" },
  { name: "Cozy Country Bed & Breakfast", group: "Food & Hospitality", primaryColor: "#b45309", iconKeyword: "bed" },
  { name: "Backpacker Urban Hostel", group: "Food & Hospitality", primaryColor: "#059669", iconKeyword: "map" },
  { name: "Vacation Rental Host", group: "Food & Hospitality", primaryColor: "#f97316", iconKeyword: "home" },
  { name: "Five-Star Luxury Resort", group: "Food & Hospitality", primaryColor: "#172554", iconKeyword: "palmtree" },
  { name: "Ocean Yacht Cruise Charter", group: "Food & Hospitality", primaryColor: "#0284c7", iconKeyword: "ship" },
  { name: "Industrial Event Venue Space", group: "Food & Hospitality", primaryColor: "#334155", iconKeyword: "warehouse" },
  { name: "Traditional Matcha Tea House", group: "Food & Hospitality", primaryColor: "#15803d", iconKeyword: "leaf" },
  { name: "Gourmet Glazed Donut Shop", group: "Food & Hospitality", primaryColor: "#db2777", iconKeyword: "donut" },
  { name: "Gluten-Free Bakery Cafe", group: "Food & Hospitality", primaryColor: "#d97706", iconKeyword: "cake" },
  { name: "Authentic Korean BBQ Table", group: "Food & Hospitality", primaryColor: "#b91c1c", iconKeyword: "flame" },
  { name: "Organic Vegan Kitchen", group: "Food & Hospitality", primaryColor: "#16a34a", iconKeyword: "carrot" },
  { name: "Streetside Taqueria", group: "Food & Hospitality", primaryColor: "#ea580c", iconKeyword: "taco" },
  { name: "Dry-Aged Steakhouse Club", group: "Food & Hospitality", primaryColor: "#7f1d1d", iconKeyword: "meet" },
  { name: "Community Food Co-Op", group: "Food & Hospitality", primaryColor: "#059669", iconKeyword: "users" },
  { name: "Pastry & Dessert Bar", group: "Food & Hospitality", primaryColor: "#be185d", iconKeyword: "swatch" },
  { name: "Traditional Sweet Stall", group: "Food & Hospitality", primaryColor: "#c2410c", iconKeyword: "sweet" },
  { name: "Fresh Tossed Salad Hub", group: "Food & Hospitality", primaryColor: "#10b981", iconKeyword: "salad" },
  { name: "Gastronomic Tavern & Inn", group: "Food & Hospitality", primaryColor: "#0f172a", iconKeyword: "door" },

  // --- Group 3: Medical & Wellness (30) ---
  { name: "Family Health Practice", group: "Medical & Wellness", primaryColor: "#0284c7", iconKeyword: "stethoscope" },
  { name: "Pediatric Care Clinic", group: "Medical & Wellness", primaryColor: "#06b6d4", iconKeyword: "baby" },
  { name: "Orthodontic & Dental Surgery", group: "Medical & Wellness", primaryColor: "#0f766e", iconKeyword: "sparkles" },
  { name: "Spine Chiropractic Center", group: "Medical & Wellness", primaryColor: "#1e3a8a", iconKeyword: "activity" },
  { name: "Sports Physiotherapy Clinic", group: "Medical & Wellness", primaryColor: "#0d9488", iconKeyword: "shield" },
  { name: "Optometry Vision Lounge", group: "Medical & Wellness", primaryColor: "#0369a1", iconKeyword: "eye" },
  { name: "Laser Dermatology Suite", group: "Medical & Wellness", primaryColor: "#db2777", iconKeyword: "check" },
  { name: "Comprehensive Veterinary ER", group: "Medical & Wellness", primaryColor: "#059669", iconKeyword: "heart" },
  { name: "Mental Psychiatry Hub", group: "Medical & Wellness", primaryColor: "#4f46e5", iconKeyword: "brain" },
  { name: "Metropolitan Cardiology Unit", group: "Medical & Wellness", primaryColor: "#b91c1c", iconKeyword: "pulse" },
  { name: "Vinyasa Flow Yoga Space", group: "Medical & Wellness", primaryColor: "#16a34a", iconKeyword: "wind" },
  { name: "High Intensity Power Gym", group: "Medical & Wellness", primaryColor: "#27272a", iconKeyword: "swords" },
  { name: "Aroma Therapy Day Spa", group: "Medical & Wellness", primaryColor: "#0d9488", iconKeyword: "flower" },
  { name: "Classical Pilates Annex", group: "Medical & Wellness", primaryColor: "#14b8a6", iconKeyword: "accessibility" },
  { name: "Registered Nutritionist Clinic", group: "Medical & Wellness", primaryColor: "#10b981", iconKeyword: "apple" },
  { name: "Therapeutic Massage Suite", group: "Medical & Wellness", primaryColor: "#4d7c0f", iconKeyword: "hand" },
  { name: "Traditional Acupuncture Clinic", group: "Medical & Wellness", primaryColor: "#15803d", iconKeyword: "cross" },
  { name: "Orthotic Podiatry Care", group: "Medical & Wellness", primaryColor: "#475569", iconKeyword: "foot-icon" },
  { name: "Pedodontic Kids Dental", group: "Medical & Wellness", primaryColor: "#0891b2", iconKeyword: "smile" },
  { name: "Audiology & Hearing Aid Clinic", group: "Medical & Wellness", primaryColor: "#0284c7", iconKeyword: "ear" },
  { name: "Stroke Rehabilitation Center", group: "Medical & Wellness", primaryColor: "#0f766e", iconKeyword: "user-check" },
  { name: "Anti-Aging Wellness Retreat", group: "Medical & Wellness", primaryColor: "#0369a1", iconKeyword: "sun" },
  { name: "Pathological Clinical Laboratory", group: "Medical & Wellness", primaryColor: "#1e293b", iconKeyword: "flask" },
  { name: "Prescription Care Pharmacist", group: "Medical & Wellness", primaryColor: "#0d9488", iconKeyword: "shield-alert" },
  { name: "Family & Marriage Counseling", group: "Medical & Wellness", primaryColor: "#4f46e5", iconKeyword: "heart" },
  { name: "Assisted Senior Care Living", group: "Medical & Wellness", primaryColor: "#b45309", iconKeyword: "home" },
  { name: "Holistic Midwifery Center", group: "Medical & Wellness", primaryColor: "#ec4899", iconKeyword: "users" },
  { name: "Naturopathic Healing Clinic", group: "Medical & Wellness", primaryColor: "#059669", iconKeyword: "leaf" },
  { name: "Sleep Lab Polysomnography", group: "Medical & Wellness", primaryColor: "#1e1b4b", iconKeyword: "moon" },
  { name: "Hyperbaric Oxygen Chamber", group: "Medical & Wellness", primaryColor: "#3b82f6", iconKeyword: "cloud" },

  // --- Group 4: Professional Services (30) ---
  { name: "Corporate Litigation Law Firm", group: "Professional Services", primaryColor: "#1e1b4b", iconKeyword: "briefcase" },
  { name: "Chartered CPA Accounting Office", group: "Professional Services", primaryColor: "#1e293b", iconKeyword: "calculator" },
  { name: "Digital Growth Marketing Studio", group: "Professional Services", primaryColor: "#4f46e5", iconKeyword: "trending-up" },
  { name: "Aesthetic Architectural Studio", group: "Professional Services", primaryColor: "#475569", iconKeyword: "ruler" },
  { name: "Executive Recruitment Agency", group: "Professional Services", primaryColor: "#0369a1", iconKeyword: "user-plus" },
  { name: "Heritage Insurance Brokerage", group: "Professional Services", primaryColor: "#1e3a8a", iconKeyword: "key" },
  { name: "Commercial Real Estate Broker", group: "Professional Services", primaryColor: "#854d0e", iconKeyword: "home" },
  { name: "Enterprise Strategy Consultancy", group: "Professional Services", primaryColor: "#0f766e", iconKeyword: "line-chart" },
  { name: "Global PR & Communications", group: "Professional Services", primaryColor: "#7c3aed", iconKeyword: "megaphone" },
  { name: "Managed IT & Network Security", group: "Professional Services", primaryColor: "#2563eb", iconKeyword: "server" },
  { name: "Corporate Guard Security Services", group: "Professional Services", primaryColor: "#1f2937", iconKeyword: "shield" },
  { name: "Multilingual Legal Translation", group: "Professional Services", primaryColor: "#0891b2", iconKeyword: "languages" },
  { name: "Industrial Deep Cleaning Corp", group: "Professional Services", primaryColor: "#06b6d4", iconKeyword: "sparkles" },
  { name: "Dry Cleaning & Valet Laundry", group: "Professional Services", primaryColor: "#2563eb", iconKeyword: "shirt" },
  { name: "Metropolitan Pest Control Inc", group: "Professional Services", primaryColor: "#ca8a04", iconKeyword: "bug" },
  { name: "Waste Disposal & Recycling Log", group: "Professional Services", primaryColor: "#15803d", iconKeyword: "recycle" },
  { name: "Private Wealth & Tax Advisor", group: "Professional Services", primaryColor: "#334155", iconKeyword: "coin" },
  { name: "Executive Leadership Coaching", group: "Professional Services", primaryColor: "#db2777", iconKeyword: "award" },
  { name: "Retirement Planner & Wealth Coach", group: "Professional Services", primaryColor: "#047857", iconKeyword: "wallet" },
  { name: "Wedding & Gala Coordinator", group: "Professional Services", primaryColor: "#ec4899", iconKeyword: "gift" },
  { name: "Exotic Travel Agency Studio", group: "Professional Services", primaryColor: "#0284c7", iconKeyword: "plane" },
  { name: "Vance Private Investigations", group: "Professional Services", primaryColor: "#0f172a", iconKeyword: "spy" },
  { name: "Certified Legal Notary Office", group: "Professional Services", primaryColor: "#1e293b", iconKeyword: "pen" },
  { name: "Maritime Customs Broker", group: "Professional Services", primaryColor: "#0f766e", iconKeyword: "anchor" },
  { name: "Outsourced Human Resources Lab", group: "Professional Services", primaryColor: "#4f46e5", iconKeyword: "users" },
  { name: "Precision HVAC System Design", group: "Professional Services", primaryColor: "#475569", iconKeyword: "settings" },
  { name: "Commercial Asset Appraiser", group: "Professional Services", primaryColor: "#a16207", iconKeyword: "coins" },
  { name: "Interior Space Designer", group: "Professional Services", primaryColor: "#be185d", iconKeyword: "layout" },
  { name: "Virtual Executive Assistant", group: "Professional Services", primaryColor: "#0d9488", iconKeyword: "user" },
  { name: "Fashion & Product Photography", group: "Professional Services", primaryColor: "#be185d", iconKeyword: "camera" },

  // --- Group 5: Logistics & Industrial (30) ---
  { name: "Intermodal Freight Forwarder", group: "Logistics & Industrial", primaryColor: "#374151", iconKeyword: "container" },
  { name: "Climate Controlled Warehousing", group: "Logistics & Industrial", primaryColor: "#475569", iconKeyword: "warehouse" },
  { name: "Express Local Courier Dispatch", group: "Logistics & Industrial", primaryColor: "#0284c7", iconKeyword: "milestone" },
  { name: "Heavy Haulage Trucking Lines", group: "Logistics & Industrial", primaryColor: "#f97316", iconKeyword: "truck" },
  { name: "Oceanic Cargo Shipping Lines", group: "Logistics & Industrial", primaryColor: "#1d4ed8", iconKeyword: "ship" },
  { name: "E-Commerce Fulfillment Depot", group: "Logistics & Industrial", primaryColor: "#4f46e5", iconKeyword: "package" },
  { name: "Customs Entry & Port Clearance", group: "Logistics & Industrial", primaryColor: "#111827", iconKeyword: "file-check" },
  { name: "Industrial Crating & Packing", group: "Logistics & Industrial", primaryColor: "#7c2d12", iconKeyword: "box" },
  { name: "Heavy Crane & Rigging Rental", group: "Logistics & Industrial", primaryColor: "#e11d48", iconKeyword: "hammer" },
  { name: "Precision CNC Sheet Metal Fab", group: "Logistics & Industrial", primaryColor: "#0f172a", iconKeyword: "anvil" },
  { name: "Industrial Thermoplastics Injection", group: "Logistics & Industrial", primaryColor: "#5b21b6", iconKeyword: "layers" },
  { name: "Multi-Axis Milling Machine lab", group: "Logistics & Industrial", primaryColor: "#1e293b", iconKeyword: "settings" },
  { name: "High Voltage Electrical Systems", group: "Logistics & Industrial", primaryColor: "#ea580c", iconKeyword: "bolt" },
  { name: "Commercial High-Flow Plumbing", group: "Logistics & Industrial", primaryColor: "#2563eb", iconKeyword: "droplet" },
  { name: "Clean Air HVAC Maintenance", group: "Logistics & Industrial", primaryColor: "#0891b2", iconKeyword: "wind" },
  { name: "General Commercial Contractor", group: "Logistics & Industrial", primaryColor: "#475569", iconKeyword: "brick" },
  { name: "Acoustic Insulation Contractor", group: "Logistics & Industrial", primaryColor: "#0d9488", iconKeyword: "radio" },
  { name: "Industrial Tubing Scaffolding", group: "Logistics & Industrial", primaryColor: "#3f3f46", iconKeyword: "grid" },
  { name: "Bricklaying & Stonework Group", group: "Logistics & Industrial", primaryColor: "#78350f", iconKeyword: "square" },
  { name: "Lawn & Arborist Landscaping", group: "Logistics & Industrial", primaryColor: "#15803d", iconKeyword: "tree" },
  { name: "Grid-Tie Solar Array Installers", group: "Logistics & Industrial", primaryColor: "#d97706", iconKeyword: "sun" },
  { name: "Electrostatic Powder Coating", group: "Logistics & Industrial", primaryColor: "#0f172a", iconKeyword: "paint-bucket" },
  { name: "Reinforced Concrete Foundation", group: "Logistics & Industrial", primaryColor: "#1e293b", iconKeyword: "hard-hat" },
  { name: "Commercial Demolition Crew", group: "Logistics & Industrial", primaryColor: "#e11d48", iconKeyword: "flame" },
  { name: "Thermal Imaging CCTV Security", group: "Logistics & Industrial", primaryColor: "#1e3a8a", iconKeyword: "camera" },
  { name: "Long-Haul Cold Chain Transport", group: "Logistics & Industrial", primaryColor: "#0284c7", iconKeyword: "snowflake" },
  { name: "Inland Dry Port Freight Handler", group: "Logistics & Industrial", primaryColor: "#0f766e", iconKeyword: "anchor" },
  { name: "Double-Stack Intermodal Rail Cargo", group: "Logistics & Industrial", primaryColor: "#334155", iconKeyword: "subway" },
  { name: "Air Cargo Terminal Handling", group: "Logistics & Industrial", primaryColor: "#2563eb", iconKeyword: "plane-takeoff" },
  { name: "Asphalt Paving & Civil Works", group: "Logistics & Industrial", primaryColor: "#18181b", iconKeyword: "map" },

  // --- Group 6: Tech, Design & Academic (30) ---
  { name: "Multi-Tenant SaaS Provider", group: "Tech, Design & Academic", primaryColor: "#4f46e5", iconKeyword: "rocket" },
  { name: "Kubernetes Cloud Architect", group: "Tech, Design & Academic", primaryColor: "#0284c7", iconKeyword: "cloud-lightning" },
  { name: "Interactive React Web Studio", group: "Tech, Design & Academic", primaryColor: "#0ea5e9", iconKeyword: "code" },
  { name: "Technical SEO Optimization Suite", group: "Tech, Design & Academic", primaryColor: "#059669", iconKeyword: "line-chart" },
  { name: "Cross-platform Game Development", group: "Tech, Design & Academic", primaryColor: "#db2777", iconKeyword: "gamepad-2" },
  { name: "Zero-Trust Cybersecurity lab", group: "Tech, Design & Academic", primaryColor: "#ef4444", iconKeyword: "lock" },
  { name: "Large Language Model AI Labs", group: "Tech, Design & Academic", primaryColor: "#7c3aed", iconKeyword: "brain-circuit" },
  { name: "Telemetry Data & Analytics Inc", group: "Tech, Design & Academic", primaryColor: "#10b981", iconKeyword: "bar-chart" },
  { name: "Universal Mobile App Studio", group: "Tech, Design & Academic", primaryColor: "#2563eb", iconKeyword: "smartphone" },
  { name: "Local Computer Hardware Repair", group: "Tech, Design & Academic", primaryColor: "#475569", iconKeyword: "laptop" },
  { name: "Ivy League STEM Tutoring", group: "Tech, Design & Academic", primaryColor: "#b91c1c", iconKeyword: "graduation-cap" },
  { name: "Academy of Ballet & Dance", group: "Tech, Design & Academic", primaryColor: "#ec4899", iconKeyword: "accessibility" },
  { name: "Commercial Driver License Academy", group: "Tech, Design & Academic", primaryColor: "#111827", iconKeyword: "truck" },
  { name: "Karate & Self Defense Dojo", group: "Tech, Design & Academic", primaryColor: "#b91c1c", iconKeyword: "target" },
  { name: "Full-Time Science Prep Institute", group: "Tech, Design & Academic", primaryColor: "#0e7490", iconKeyword: "library" },
  { name: "Dev Academy Coding Boot Camp", group: "Tech, Design & Academic", primaryColor: "#4f46e5", iconKeyword: "terminal" },
  { name: "Fluency Spanish Language Academy", group: "Tech, Design & Academic", primaryColor: "#ca8a04", iconKeyword: "navigation" },
  { name: "Olympic Swim Stroke Academy", group: "Tech, Design & Academic", primaryColor: "#0284c7", iconKeyword: "droplets" },
  { name: "Classical Cello Music Academy", group: "Tech, Design & Academic", primaryColor: "#78350f", iconKeyword: "music" },
  { name: "Advanced Robotics & Automation Lab", group: "Tech, Design & Academic", primaryColor: "#0f172a", iconKeyword: "toy-brick" },
  { name: "Large Format Letterpress Printing", group: "Tech, Design & Academic", primaryColor: "#1f2937", iconKeyword: "printer" },
  { name: "Swiss Minimalist Design Studio", group: "Tech, Design & Academic", primaryColor: "#111827", iconKeyword: "raster" },
  { name: "SaaS & Product Copywriting Hub", group: "Tech, Design & Academic", primaryColor: "#0d9488", iconKeyword: "pen-tool" },
  { name: "Commercial & 3D Video Production", group: "Tech, Design & Academic", primaryColor: "#e11d48", iconKeyword: "video" },
  { name: "Binaural Audiobook Recording Lab", group: "Tech, Design & Academic", primaryColor: "#7c3aed", iconKeyword: "mic" },
  { name: "Defense Aerospace Lab Research", group: "Tech, Design & Academic", primaryColor: "#1e293b", iconKeyword: "orbit" },
  { name: "Global Patent & Trademark Firm", group: "Tech, Design & Academic", primaryColor: "#075985", iconKeyword: "file-text" },
  { name: "Interaction UI Prototyping Lab", group: "Tech, Design & Academic", primaryColor: "#4f46e5", iconKeyword: "columns" },
  { name: "Broadcasting Audio Services", group: "Tech, Design & Academic", primaryColor: "#2563eb", iconKeyword: "disc" },
  { name: "Corporate Event AV Production", group: "Tech, Design & Academic", primaryColor: "#1e1b4b", iconKeyword: "speaker" }
];

// Helper to define dynamic high-fidelity presets for each of the 180 categories
export interface CategorySpecs {
  items: { description: string; qty: number; rate: number; sku: string }[];
  taxRate: number;
  stampText: string;
  stampShape: 'circle' | 'oval' | 'rectangle' | 'badge' | 'pill';
}

export function getIndustrySpecsForCategory(categoryName: string): CategorySpecs {
  const nameLower = categoryName.toLowerCase();
  
  // Find metadata to get group and contextual fallbacks
  const meta = INDUSTRY_CATEGORIES_METADATA.find(c => c.name === categoryName);
  const group = meta ? meta.group : "Professional Services" as const;

  // Exact static category definitions first to protect custom initial features
  if (categoryName === "Grocery Supermarket") {
    return {
      items: [
        { description: "Organic Honeycrisp Apples (Bag)", qty: 8, rate: 3.49, sku: "SKU-HCA-08" },
        { description: "Grass-Fed Ribeye Steak (USDA Choice)", qty: 2, rate: 19.99, sku: "SKU-RBY-02" },
        { description: "Cold-Pressed Artisan Olive Oil 750ml", qty: 1, rate: 24.50, sku: "SKU-AOO-01" }
      ],
      taxRate: 6.0,
      stampText: "ITEMS DELIVERED",
      stampShape: "rectangle"
    };
  }
  if (categoryName === "Fashion Apparel Boutique") {
    return {
      items: [
        { description: "Velvet Hour Silk Slip Dress (Emerald)", qty: 1, rate: 380.00, sku: "SKU-VSS-01" },
        { description: "Guise Handcrafted Brass Hoops Pair", qty: 1, rate: 110.00, sku: "SKU-GBH-01" },
        { description: "Tailored Linen Blazer (Pearl Sand)", qty: 1, rate: 295.00, sku: "SKU-TLB-01" }
      ],
      taxRate: 8.5,
      stampText: "FINAL SALE",
      stampShape: "badge"
    };
  }
  if (categoryName === "Independent Bookstore") {
    return {
      items: [
        { description: "The Odyssey (Fagles Trans, Hardcover)", qty: 1, rate: 45.00, sku: "SKU-ODY-01" },
        { description: "Anatomy of Melancholy Trilogy Box Set", qty: 1, rate: 120.00, sku: "SKU-AMY-V3" },
        { description: "Modern Literary Hermeneutics Bundle", qty: 3, rate: 29.99, sku: "SKU-MLT-BDL" }
      ],
      taxRate: 5.0,
      stampText: "BOUGHT",
      stampShape: "circle"
    };
  }

  // Define dynamic keyword clusters that map all 180 niches beautifully
  const rules = [
    {
      keys: ["grocery", "supermarket"],
      items: [
        { description: "Organic Honeycrisp Apples (Bag)", qty: 8, rate: 3.49, sku: "SKU-HCA-08" },
        { description: "Grass-Fed Ribeye Steak (USDA Choice)", qty: 2, rate: 19.99, sku: "SKU-RBY-02" },
        { description: "Cold-Pressed Artisan Olive Oil 750ml", qty: 1, rate: 24.50, sku: "SKU-AOO-01" }
      ],
      taxRate: 6.0,
      stampText: "GROCERY DISPATCH",
      stampShape: "rectangle" as const
    },
    {
      keys: ["fashion", "apparel", "boutique", "consignment", "wardrobe", "jewelry", "leather", "footwear"],
      items: [
        { description: "Premium Tailored Leather/Silk Dress", qty: 1, rate: 380.00, sku: "SKU-TXD-01" },
        { description: "Handcrafted Silver Hoop Earrings Pair", qty: 1, rate: 110.00, sku: "SKU-SEP-02" },
        { description: "Italian Calfskin Leather Boots / Coat", qty: 1, rate: 295.00, sku: "SKU-ILB-03" }
      ],
      taxRate: 8.5,
      stampText: "AUTHENTICITY SEED",
      stampShape: "badge" as const
    },
    {
      keys: ["bookstore", "comic", "stationery", "planning", "library"],
      items: [
        { description: "Symmetric Dot-Grid Hardcover Journals", qty: 5, rate: 15.00, sku: "SKU-DGJ-05" },
        { description: "Rare Silver Age Anthology / Novel Trio", qty: 1, rate: 120.00, sku: "SKU-RSN-01" },
        { description: "Premium Refillable Fountain Pen Ink (Set)", qty: 2, rate: 29.99, sku: "SKU-FPI-02" }
      ],
      taxRate: 5.0,
      stampText: "PAGES REGISTERED",
      stampShape: "circle" as const
    },
    {
      keys: ["florist", "garden", "nursery", "botanical", "landscap", "arborist", "lawn", "tree"],
      items: [
        { description: "Seasonal Wildflower Bridal Bouquet", qty: 2, rate: 85.00, sku: "SKU-SWB-02" },
        { description: "Handfired Clay Terra Cotta Planters", qty: 4, rate: 24.00, sku: "SKU-TCP-04" },
        { description: "Bio-Active Slow Release Compost Blend", qty: 3, rate: 12.50, sku: "SKU-BCO-03" }
      ],
      taxRate: 7.0,
      stampText: "FRESHLY CUT",
      stampShape: "oval" as const
    },
    {
      keys: ["toy", "children"],
      items: [
        { description: "Handcrafted Waldorf Wooden Play Set", qty: 1, rate: 125.00, sku: "SKU-WPS-01" },
        { description: "Magnetic Translucent Building Tiles (100pc)", qty: 2, rate: 49.99, sku: "SKU-MBT-02" },
        { description: "Hypoallergenic Organic Cotton Toy Plush", qty: 1, rate: 35.00, sku: "SKU-OCT-01" }
      ],
      taxRate: 6.5,
      stampText: "KIDS COMPLIANT",
      stampShape: "circle" as const
    },
    {
      keys: ["electronic", "hardware repair", "it & network", "hardware outlet", "computer & electronics"],
      items: [
        { description: "Enterprise Core Network Router Switch", qty: 1, rate: 450.00, sku: "SKU-ERS-01" },
        { description: "True Wireless Active Noise Earbuds", qty: 2, rate: 125.00, sku: "SKU-TWE-02" },
        { description: "Aero USB-C Heavy Hub Docking Station", qty: 3, rate: 85.00, sku: "SKU-UDS-03" }
      ],
      taxRate: 9.0,
      stampText: "CORE SECURED",
      stampShape: "rectangle" as const
    },
    {
      keys: ["bakery", "pastry", "donut", "sweet", "cake"],
      items: [
        { description: "Artisan Organic Sourdough Boules (Tray)", qty: 6, rate: 7.50, sku: "SKU-ASB-06" },
        { description: "Gourmet Almond Pastries & Croissants", qty: 12, rate: 4.25, sku: "SKU-APC-12" },
        { description: "Madagascar Vanilla Custard Birthday Cake", qty: 1, rate: 65.00, sku: "SKU-MVC-01" }
      ],
      taxRate: 6.0,
      stampText: "BAKED FRESH",
      stampShape: "oval" as const
    },
    {
      keys: ["butcher", "meat", "fishmonger", "fresh catch"],
      items: [
        { description: "Dry-Aged Grade A Wagyu Steak Bone-In", qty: 2, rate: 95.00, sku: "SKU-WAG-02" },
        { description: "Free-Range Organic Thick Pork Chops", qty: 4, rate: 18.50, sku: "SKU-PCH-04" },
        { description: "Fresh Caught Sockeye Salmon Steaks", qty: 3, rate: 24.00, sku: "SKU-SAL-03" }
      ],
      taxRate: 5.5,
      stampText: "MEAT CHECKED",
      stampShape: "rectangle" as const
    },
    {
      keys: ["wine", "spirits", "cellar", "microbrewery", "speakeasy", "cocktail", "beer"],
      items: [
        { description: "Special Single Barrel Islay Scotch 18yr", qty: 1, rate: 185.00, sku: "SKU-SBS-01" },
        { description: "Reserve Napa Cabernet Sauvignon 2016", qty: 3, rate: 65.00, sku: "SKU-NCS-03" },
        { description: "Artisan Crystal Glencairn Glassware Set", qty: 1, rate: 45.00, sku: "SKU-CGS-01" }
      ],
      taxRate: 20.0,
      stampText: "SPIRITS CERT",
      stampShape: "badge" as const
    },
    {
      keys: ["apothecary", "pharmacy", "pharmacist"],
      items: [
        { description: "Custom Multi-Herb Tincture Immune Drops", qty: 2, rate: 35.00, sku: "SKU-ITD-02" },
        { description: "Organic Botanical Facial Serum Mist", qty: 2, rate: 45.00, sku: "SKU-BFM-02" },
        { description: "Standard Clinical Prescription Deductible", qty: 1, rate: 25.00, sku: "SKU-CPD-01" }
      ],
      taxRate: 2.5,
      stampText: "FORMULA VERIFIED",
      stampShape: "circle" as const
    },
    {
      keys: ["pet"],
      items: [
        { description: "Premium Wild Grain-Free Kibble (25lb Bag)", qty: 2, rate: 68.00, sku: "SKU-WGK-02" },
        { description: "Organic Sisal Braided Cat Scratch Post", qty: 1, rate: 45.00, sku: "SKU-CSP-01" },
        { description: "Triple Omega Alaskan Salmon Oil Dropper", qty: 2, rate: 24.50, sku: "SKU-ASO-02" }
      ],
      taxRate: 8.0,
      stampText: "PET CARE PASS",
      stampShape: "pill" as const
    },
    {
      keys: ["bicycle", "bike"],
      items: [
        { description: "Pro-Aero Carbon Fiber Bicycle Frameset", qty: 1, rate: 1450.00, sku: "SKU-CBF-01" },
        { description: "Tubeless Ready Carbon Wheelselect Pair", qty: 1, rate: 750.00, sku: "SKU-CWP-01" },
        { description: "Full Hub Hydraulic Calibration & Grease", qty: 1, rate: 120.00, sku: "SKU-HCG-01" }
      ],
      taxRate: 8.5,
      stampText: "STUDIO TRACKED",
      stampShape: "badge" as const
    },
    {
      keys: ["furniture", "showroom", "sofa"],
      items: [
        { description: "Mid-Century Walnut Credenza & Buffet Console", qty: 1, rate: 1250.00, sku: "SKU-WCC-01" },
        { description: "Bouclé Textured Accent Armchair (Sand)", qty: 2, rate: 395.00, sku: "SKU-BAA-02" },
        { description: "Minimalist Brushed Brass Floor Lamp", qty: 1, rate: 180.00, sku: "SKU-BFL-01" }
      ],
      taxRate: 10.0,
      stampText: "INTERIOR READY",
      stampShape: "rectangle" as const
    },
    {
      keys: ["hardware", "plumbing", "hvac", "insulation", "paving", "civil", "demolition", "contract", "scaffold", "masonry", "electrical", "concrete", "milling", "cnc", "metal fab", "builder"],
      items: [
        { description: "Heavy Duty Brushless 20V Drill Combo Kit", qty: 1, rate: 249.00, sku: "SKU-BDK-01" },
        { description: "Premium Copper Plumbing Couplings & Pipes", qty: 12, rate: 11.50, sku: "SKU-CPC-12" },
        { description: "Eco-Fiber Thermal Rolled Insulation 100sqft", qty: 5, rate: 45.00, sku: "SKU-ETI-05" }
      ],
      taxRate: 8.25,
      stampText: "SUPPLY APPROVED",
      stampShape: "rectangle" as const
    },
    {
      keys: ["music", "instrument", "cello", "broadcasting"],
      items: [
        { description: "Solid Spruce Acoustic Guitar with Hardcase", qty: 1, rate: 580.00, sku: "SKU-SAG-01" },
        { description: "Weighted Hammer USB MIDI Keyboard 88-Key", qty: 1, rate: 399.00, sku: "SKU-WKB-01" },
        { description: "Premium Studio Shield Instrument Cables", qty: 4, rate: 25.00, sku: "SKU-SIC-04" }
      ],
      taxRate: 8.0,
      stampText: "TONE PASSED",
      stampShape: "badge" as const
    },
    {
      keys: ["antique"],
      items: [
        { description: "Restored 19th Century French Walnut Clock", qty: 1, rate: 950.00, sku: "SKU-FWC-01" },
        { description: "Vitreous Cobalt Handcrafted Porcelain Urn", qty: 1, rate: 350.00, sku: "SKU-HPU-01" },
        { description: "Premium Conservation Polishing & Sealant", qty: 1, rate: 120.00, sku: "SKU-CPS-01" }
      ],
      taxRate: 12.0,
      stampText: "ARCHIVAL CERT",
      stampShape: "circle" as const
    },
    {
      keys: ["cosmetics", "beauty"],
      items: [
        { description: "Organic Calendula Essential Skincare Serum", qty: 3, rate: 48.00, sku: "SKU-CSS-03" },
        { description: "Sandalwood Infused Clay Exfoliating Scrub", qty: 2, rate: 32.00, sku: "SKU-CES-02" },
        { description: "Botanical Hydrosol Face Toner & Mist", qty: 4, rate: 18.00, sku: "SKU-HTM-04" }
      ],
      taxRate: 10.0,
      stampText: "SALON APPROVED",
      stampShape: "oval" as const
    },
    {
      keys: ["souvenir", "gift"],
      items: [
        { description: "Handpainted Local Wildlife Ceramic Mug", qty: 10, rate: 12.50, sku: "SKU-WCM-10" },
        { description: "Cotton Embroidered Regional Crest Tote", qty: 5, rate: 18.00, sku: "SKU-RCT-05" },
        { description: "Premium Local Honey and Maple Gift Box", qty: 2, rate: 45.00, sku: "SKU-HGB-02" }
      ],
      taxRate: 8.0,
      stampText: "GIFTS VERIFIED",
      stampShape: "pill" as const
    },
    {
      keys: ["optical", "optometry", "eye", "glasses", "vision"],
      items: [
        { description: "Premium Acetate Optical Spectacle Frame", qty: 1, rate: 240.00, sku: "SKU-OSF-01" },
        { description: "Blue-Light Blocking Polycarbonate Lenses", qty: 1, rate: 150.00, sku: "SKU-PBL-01" },
        { description: "Premium Anti-Fog Scratch Resistant Shield", qty: 1, rate: 45.00, sku: "SKU-SRS-01" }
      ],
      taxRate: 4.5,
      stampText: "VISION CLEAR",
      stampShape: "rectangle" as const
    },
    {
      keys: ["watchmaker"],
      items: [
        { description: "Swiss Automatic Chronograph Balance Service", qty: 1, rate: 350.00, sku: "SKU-SCS-01" },
        { description: "Full Case Gasket Hermetic Pressure Seal", qty: 1, rate: 85.00, sku: "SKU-HPS-01" },
        { description: "Sub-Second Escapement Calibration Index", qty: 1, rate: 110.00, sku: "SKU-ECI-01" }
      ],
      taxRate: 10.0,
      stampText: "TIME CERTIFIED",
      stampShape: "circle" as const
    },
    {
      keys: ["boardgame"],
      items: [
        { description: "Multiplayer Strategic Boardgame (Deluxe)", qty: 1, rate: 85.00, sku: "SKU-SBD-01" },
        { description: "Heavy-Duty Neoprene Table Player Mat", qty: 2, rate: 35.00, sku: "SKU-NPM-02" },
        { description: "Acid-Free Transparent Card Protectors (500)", qty: 1, rate: 18.00, sku: "SKU-TCP-500" }
      ],
      taxRate: 8.0,
      stampText: "TABLE APPROVED",
      stampShape: "pill" as const
    },
    {
      keys: ["art supply", "canvas", "easel", "palette", "fine art"],
      items: [
        { description: "Professional Heavy-Body Acrylic Paints (Set of 10)", qty: 2, rate: 75.00, sku: "SKU-APS-02" },
        { description: "Double-Stretched Premium Belgian Cotton Linen Canvas", qty: 4, rate: 35.00, sku: "SKU-BCC-04" },
        { description: "Finest Red Sable Hair Detail Brushes (Set)", qty: 1, rate: 58.00, sku: "SKU-SDB-01" }
      ],
      taxRate: 8.0,
      stampText: "CREATOR STAMP",
      stampShape: "oval" as const
    },
    {
      keys: ["bistro", "french bistro", "pizzeria", "sushi", "burger", "steakhouse", "taqueria", "salad", "food truck", "banquets", "catering", "dining", "kitchen", "tavern"],
      items: [
        { description: "Four-Course Gourmet Chef Tasting Menu Plate", qty: 2, rate: 115.00, sku: "SKU-CTM-02" },
        { description: "Artisanal Charcoal Grilled Premium Ribeye Entree", qty: 2, rate: 45.00, sku: "SKU-PRE-02" },
        { description: "Reserved Sommelier Pairing Wine Bottle", qty: 1, rate: 75.00, sku: "SKU-SPW-01" }
      ],
      taxRate: 14.0,
      stampText: "DINING RESERVED",
      stampShape: "oval" as const
    },
    {
      keys: ["hotel", "resort", "bed & breakfast", "accommodation", "hostel", "rental", "yacht", "cruise", "venue"],
      items: [
        { description: "Classic Double Bed Suite Suite 3 Nights", qty: 3, rate: 220.00, sku: "SKU-DBS-03" },
        { description: "Full Onsite Therapeutic Massage & Wellness Package", qty: 1, rate: 160.00, sku: "SKU-TMP-01" },
        { description: "Curate Excursion Transport & Gourmet Breakfast Pass", qty: 2, rate: 45.00, sku: "SKU-EBP-02" }
      ],
      taxRate: 16.0,
      stampText: "ROOM PAID CLOSED",
      stampShape: "badge" as const
    },
    {
      keys: ["tea house", "matcha", "coffee", "roasters", "smoothie", "juice"],
      items: [
        { description: "Freshly Roasted Organic Coffee Espresso Beans (1KG)", qty: 4, rate: 22.00, sku: "SKU-REB-04" },
        { description: "Grade-A Ceremonial Kyoto Stoneground Matcha", qty: 1, rate: 48.00, sku: "SKU-KSM-01" },
        { description: "Cold-Pressed Mineral Hydro-Juice Packets", qty: 15, rate: 4.50, sku: "SKU-HJP-15" }
      ],
      taxRate: 7.5,
      stampText: "BREW SEALED",
      stampShape: "circle" as const
    },
    {
      keys: ["vet", "veterinary"],
      items: [
        { description: "Onsite Outpatient Ultrasound/Diagnostic Check", qty: 1, rate: 185.00, sku: "SKU-UDC-01" },
        { description: "Emergency Blood Cell Count panel", qty: 1, rate: 140.00, sku: "SKU-BCC-01" },
        { description: "Pharmacy Formulated Subdermal Antibody Dose", qty: 2, rate: 45.00, sku: "SKU-SAD-02" }
      ],
      taxRate: 6.0,
      stampText: "VETERINARY PAS",
      stampShape: "pill" as const
    },
    {
      keys: ["dental", "orthodontic", "pedodontic"],
      items: [
        { description: "Comprehensive Mouth Panoramic Dental Radiograph", qty: 1, rate: 165.00, sku: "SKU-PDR-01" },
        { description: "Pro-Clean Tartar Removal & Fluoride Application", qty: 1, rate: 125.00, sku: "SKU-CFA-01" },
        { description: "Custom Fit Thermo-Active Alignment Retainer", qty: 2, rate: 95.00, sku: "SKU-TAR-02" }
      ],
      taxRate: 0.0,
      stampText: "DENTAL ARCHIVE",
      stampShape: "oval" as const
    },
    {
      keys: ["chiropractic", "physiotherapy", "spine", "orthotic", "podiatry", "stroke"],
      items: [
        { description: "Advanced Spine/Skeletal Joint Biomechanics manipulation", qty: 1, rate: 135.00, sku: "SKU-SJM-01" },
        { description: "Acupressure Musculoskeletal Soft Tissue Release", qty: 2, rate: 65.00, sku: "SKU-STR-02" },
        { description: "Post-Stroke Neurological Motor Calibrations", qty: 1, rate: 150.00, sku: "SKU-NMC-01" }
      ],
      taxRate: 0.0,
      stampText: "SPINE READY",
      stampShape: "oval" as const
    },
    {
      keys: ["cardiolog", "pediatric", "family health", "clinic", "practice", "psychiatry", "midwifery", "naturopathic", "healing", "wellness", "clinical laboratory", "oxygen", "sleep"],
      items: [
        { description: "Comprehensive Clinical Physical Health Examination", qty: 1, rate: 195.00, sku: "SKU-PHE-01" },
        { description: "Vascular Cardiovascular ECG Treadmill Test", qty: 1, rate: 260.00, sku: "SKU-ECG-01" },
        { description: "Immunity Enhancing Bio-Active Multivitamin Infusion", qty: 1, rate: 95.00, sku: "SKU-MVI-01" }
      ],
      taxRate: 0.0,
      stampText: "MEDICAL TRUST",
      stampShape: "badge" as const
    },
    {
      keys: ["law", "litigation", "legal", "notary", "patent"],
      items: [
        { description: "Partner Legal Retainer Consultation Advisory", qty: 10, rate: 350.00, sku: "SKU-LCA-10" },
        { description: "Municipal filing fees & Case Certification", qty: 1, rate: 580.00, sku: "SKU-MFC-01" },
        { description: "Associate Paralegal Case Document Review Fee", qty: 4, rate: 125.00, sku: "SKU-CDR-04" }
      ],
      taxRate: 12.5,
      stampText: "LAW ACCREDITED",
      stampShape: "rectangle" as const
    },
    {
      keys: ["cpa", "accounting", "tax", "wealth", "consult", "advisor", "appraiser", "recruitment", "human resources", "assistant"],
      items: [
        { description: "Chartered Corporate Account Audit Support Hours", qty: 12, rate: 185.00, sku: "SKU-CAH-12" },
        { description: "Federal & State Tax Preparation Filing Fee", qty: 1, rate: 650.00, sku: "SKU-TPF-01" },
        { description: "Comprehensive Wealth Planning Allocation Report", qty: 1, rate: 450.00, sku: "SKU-WAR-01" }
      ],
      taxRate: 12.5,
      stampText: "FUNDS VERIFIED",
      stampShape: "oval" as const
    },
    {
      keys: ["marketing", "seo", "copywriting", "pr &", "publicity", "video production", "audiobook", "photography", "camera", "photo"],
      items: [
        { description: "Omnichannel Multiplatform SEO Search Optimization campaign", qty: 1, rate: 1450.00, sku: "SKU-MOC-01" },
        { description: "High-Impact Product Copywriting & Site Content Design", qty: 5, rate: 120.00, sku: "SKU-PCD-05" },
        { description: "Premium Video Editing & Visual Effects Grading Hours", qty: 15, rate: 85.00, sku: "SKU-VEG-15" }
      ],
      taxRate: 10.0,
      stampText: "MARKET VERIFIED",
      stampShape: "oval" as const
    },
    {
      keys: ["architectural", "interior", "designer", "space designer"],
      items: [
        { description: "Schematic Blueprint Floor Plan Rendering Bundle", qty: 1, rate: 1850.00, sku: "SKU-SFB-01" },
        { description: "Interior Spatial Mock-up CAD Rendering & Texture", qty: 12, rate: 75.00, sku: "SKU-SMR-12" },
        { description: "Architectural Site Inspection & Foundation Verification", qty: 2, rate: 350.00, sku: "SKU-ASV-02" }
      ],
      taxRate: 8.5,
      stampText: "SPATIAL APPR",
      stampShape: "badge" as const
    },
    {
      keys: ["security", "guard", "cctv"],
      items: [
        { description: "Premium 4K Smart Motion Optical CCTV Camera System", qty: 1, rate: 850.00, sku: "SKU-CCS-01" },
        { description: "24/7 Security Command Center Remote Monitoring Setup", qty: 1, rate: 450.00, sku: "SKU-RMS-01" },
        { description: "Licensed Uniformed Officer Patrol Duties Service Hours", qty: 35, rate: 45.00, sku: "SKU-OPS-35" }
      ],
      taxRate: 10.0,
      stampText: "SITE SEIZED",
      stampShape: "oval" as const
    },
    {
      keys: ["cleaning", "laundry", "disposal", "pest", "recycling"],
      items: [
        { description: "Heavy Industrial Deep Extraction Cleaning Fluid Bundle", qty: 1, rate: 650.00, sku: "SKU-ICB-01" },
        { description: "Eco-Certified Waste Bio-hazard Solid Disposal fee", qty: 1, rate: 220.00, sku: "SKU-SDP-01" },
        { description: "Metropolitan Pest Control Thermal fumigation Process", qty: 2, rate: 180.00, sku: "SKU-PFP-02" }
      ],
      taxRate: 8.0,
      stampText: "HYGIENE COMP",
      stampShape: "oval" as const
    },
    {
      keys: ["freight", "cargo", "forwarder", "haulage", "trucking", "logistics", "fulfillment", "customs entry", "packing", "terminal", "rail"],
      items: [
        { description: "Intermodal Marine Shipping Container Freight Duties", qty: 2, rate: 1250.00, sku: "SKU-MCF-02" },
        { description: "Fulfillment Center Custom Palletized Stacking Yards", qty: 15, rate: 24.00, sku: "SKU-PSY-15" },
        { description: "International Customs Entry & Harbor Bureau Declaration", qty: 1, rate: 210.00, sku: "SKU-HBD-01" }
      ],
      taxRate: 6.5,
      stampText: "CARGO UNSEALED",
      stampShape: "rectangle" as const
    },
    {
      keys: ["saas", "cloud", "react", "cybersecurity", "game", "mobile", "ai labs", "telemetry", "robotics", "automation", "prototyping"],
      items: [
        { description: "Enterprise Kubernetes Node Compute Cluster Subscription", qty: 1, rate: 1450.00, sku: "SKU-KCS-01" },
        { description: "Microservices API Server React Sandbox Coding Hours", qty: 12, rate: 125.00, sku: "SKU-SCH-12" },
        { description: "Zero-Trust Penetration Network Security System Auditing", qty: 1, rate: 1800.00, sku: "SKU-NSA-01" }
      ],
      taxRate: 15.0,
      stampText: "DEV SYS ACTIVE",
      stampShape: "badge" as const
    },
    {
      keys: ["tutor", "academy", "school", "dojo", "camp", "coding", "preparatory", "ballet", "dance", "karate", "prep", "science pre"],
      items: [
        { description: "Bursar Term High-Prep Curriculum Registrar Enrollment", qty: 1, rate: 450.00, sku: "SKU-CRE-01" },
        { description: "Senior Academic Advisor Faculty Direct Studio Lessons", qty: 12, rate: 75.00, sku: "SKU-FSL-12" },
        { description: "Robotics Lab Equipment access & Licensing Keys", qty: 1, rate: 150.00, sku: "SKU-ELK-01" }
      ],
      taxRate: 0.0,
      stampText: "REGISTRAR SEAL",
      stampShape: "oval" as const
    }
  ];

  // Match keyword rule
  const matchedRule = rules.find(r => r.keys.some(k => nameLower.includes(k)));

  if (matchedRule) {
    return {
      items: matchedRule.items,
      taxRate: matchedRule.taxRate,
      stampText: matchedRule.stampText,
      stampShape: matchedRule.stampShape
    };
  }

  // Group default fallbacks if none of the 38 clusters matched
  let fbTax = 10.0;
  let fbStamp = "STATEMENT CLOSED";
  let fbShape: 'circle' | 'oval' | 'rectangle' | 'badge' | 'pill' = "rectangle";

  if (group === "Medical & Wellness") {
    fbTax = 0.0;
    fbStamp = "CO-PAY CLEAR";
    fbShape = "badge";
  } else if (group === "Food & Hospitality") {
    fbTax = 12.0;
    fbStamp = "PAID & CLOSED";
    fbShape = "circle";
  } else if (group === "Logistics & Industrial") {
    fbTax = 6.5;
    fbStamp = "CARGO PASSED";
    fbShape = "pill";
  } else if (group === "Tech, Design & Academic") {
    fbTax = 15.0;
    fbStamp = "SYSTEMS REGISTERED";
    fbShape = "badge";
  } else if (group === "Retail & Commerce") {
    fbTax = 8.0;
    fbStamp = "SOLD IN OUTLET";
    fbShape = "rectangle";
  }

  return {
    items: [
      { description: `${categoryName} - Specialized Custom Consultation`, qty: 8, rate: 150.00, sku: "SKU-CST-SVC" },
      { description: `Implementation Logistics and Quality Assurance`, qty: 1, rate: 450.00, sku: "SKU-QA-DELIVER" },
      { description: `Support Maintenance Guarantee Core SLA`, qty: 1, rate: 200.00, sku: "SKU-SLA-SUPP" }
    ],
    taxRate: fbTax,
    stampText: fbStamp,
    stampShape: fbShape
  };
}

// Helper to get a tailored list of items for each of the 180 categories
export function getIndustryItemsForCategory(categoryName: string): { description: string; qty: number; rate: number; sku: string }[] {
  return getIndustrySpecsForCategory(categoryName).items;
}

// Generate the beautiful structural invoice layout dynamically for the chosen category
export function generateIndustryTemplate(categoryName: string, pageSize: "A4" | "LETTER" | "LABEL_4X6" | "LABEL_3X2" = "A4"): ActiveTemplate {
  if (categoryName === "Computer & Electronics Store") {
    return generateIndianGSTTemplate(pageSize === "LETTER" ? "LETTER" : "A4");
  }
  const meta = INDUSTRY_CATEGORIES_METADATA.find(c => c.name === categoryName) || {
    name: categoryName,
    group: "Professional Services" as any,
    primaryColor: "#4f46e5",
    iconKeyword: "briefcase"
  };

  // Obtain custom industry specs
  const specs = getIndustrySpecsForCategory(meta.name);

  // Tailor colors & address text dynamically based on group
  let color = meta.primaryColor;
  let textPrimary = "#1e1b4b";
  let bgFill = "#f8fafc";
  let borderFill = "#cbd5e1";

  // Build custom sender address based on industry name
  const senderWords = meta.name.toUpperCase().split(" ");
  const companyName = senderWords.length > 2 ? senderWords.join(" ") : `${senderWords.join(" ")} GROUP CORP`;
  const domain = meta.name.toLowerCase().replace(/[^a-z0-9]/g, "") + ".com";
  
  const senderAddr = `${companyName}\n128 Enterprise Boulevard, Suite 500\nMetropolitan Hub 8040\nbilling@${domain}`;
  const recipientName = `Authorized Purchasing Client`;
  const recipientAddr = `100 Global Commerce Blvd\nRegional Office Park, Room A-102\naccounts@clientcorp.com`;

  const rows: TableRow[] = specs.items.map((itm, i) => ({
    id: `row-${i}`,
    values: {
      description: `${itm.description}`,
      qty: itm.qty,
      rate: itm.rate
    }
  }));

  const columns: ColumnDefinition[] = [
    { id: "1", header: `${meta.name} - Record / Details`, width: 50, align: "left", key: "description" },
    { id: "2", header: "Qty", width: 12, align: "center", key: "qty" },
    { id: "3", header: "Rate", width: 18, align: "right", key: "rate" },
    { id: "4", header: "Total", width: 20, align: "right", key: "total" }
  ];

  // Define canvas elements
  const elements: CanvasElement[] = [];

  // 1. Accent top bar
  elements.push({
    id: "ind-accent-bar",
    type: "shape",
    x: 0,
    y: 0,
    width: pageSize === "LETTER" ? 816 : 794,
    height: 12,
    opacity: 100,
    zIndex: 1,
    rotation: 0,
    backgroundColor: color,
    borderWidth: 0,
    borderRadius: 0
  });

  // 2. Company Big Logo
  elements.push({
    id: "ind-logo-lbl",
    type: "text",
    x: 50,
    y: 45,
    width: 450,
    height: 50,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 22,
    fontWeight: "bold",
    fontFamily: "Space Grotesk",
    color: color,
    textContent: companyName
  });

  // 3. Subtitle / Group tag
  elements.push({
    id: "ind-logo-sub",
    type: "text",
    x: 50,
    y: 80,
    width: 450,
    height: 20,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 10,
    fontFamily: "JetBrains Mono",
    color: "#64748b",
    textContent: `Global ${meta.group} Division Network Operations`
  });

  // 4. Sender details
  elements.push({
    id: "ind-sender-text",
    type: "text",
    x: 50,
    y: 120,
    width: 330,
    height: 85,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 11,
    fontFamily: "Inter",
    color: "#334155",
    lineHeight: 1.4,
    textContent: `SENDER LEDGER STAMP:\n${senderAddr}`
  });

  // 5. Invoice label title top right
  elements.push({
    id: "ind-title-txt",
    type: "text",
    x: 500,
    y: 20,
    width: 240,
    height: 60,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 24,
    fontWeight: "bold",
    fontFamily: "Space Grotesk",
    textAlign: "right",
    color: "#1e1b4b",
    textContent: "OFFICIAL STATEMENT"
  });

  // 6. Meta info box (Invoice #, Date, PO #, Tax ref)
  elements.push({
    id: "ind-meta-text",
    type: "text",
    x: 500,
    y: 90,
    width: 240,
    height: 110,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 10,
    fontFamily: "JetBrains Mono",
    textAlign: "right",
    color: "#475569",
    lineHeight: 1.5,
    textContent: `STATEMENT #: {invoice_number}\nDATE: {date}\nOFFICER: {staff_name}\nPO-ID: REF-{invoice_number}`
  });

  // 7. Client background panel
  elements.push({
    id: "ind-client-panel",
    type: "shape",
    x: 50,
    y: 225,
    width: pageSize === "LETTER" ? 716 : 694,
    height: 85,
    opacity: 100,
    zIndex: 2,
    rotation: 0,
    backgroundColor: "#f1f5f9",
    borderColor: "#e2e8f0",
    borderWidth: 1,
    borderRadius: 8
  });

  // 8. Client info text
  elements.push({
    id: "ind-client-txt",
    type: "text",
    x: 65,
    y: 238,
    width: pageSize === "LETTER" ? 686 : 664,
    height: 60,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 11.5,
    fontFamily: "Inter",
    color: "#1e293b",
    lineHeight: 1.4,
    textContent: `DEALER BILL TO / ACCOUNT HOLDER DETAILS:\n{client_details}`
  });

  // 9. Main Table
  elements.push({
    id: "ind-items-table",
    type: "table",
    x: 50,
    y: 330,
    width: pageSize === "LETTER" ? 716 : 694,
    height: 240,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    color: color,
    backgroundColor: "#ffffff",
    fontSize: 11,
    fontFamily: "Inter",
    columns: columns,
    rows: rows,
    taxRate: specs.taxRate,
    discountRate: 5
  });

  // 10. Vintage Approved Stamp
  elements.push({
    id: "ind-app-stamp",
    type: "stamp",
    x: 520,
    y: 820,
    width: 185,
    height: 65,
    opacity: 85,
    zIndex: 20,
    rotation: -10,
    color: color,
    stampText: specs.stampText,
    stampDraft: true,
    stampShape: specs.stampShape
  });

  // 11. Signature
  elements.push({
    id: "ind-auth-sig",
    type: "signature",
    x: 50,
    y: 720,
    width: 240,
    height: 70,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    signatureName: "{staff_name}",
    textContent: `Compliance Officer (${meta.group})`
  });

  // 12. QR Code Scanner linking to check status
  elements.push({
    id: "ind-qr-code",
    type: "qr",
    x: 50,
    y: 810,
    width: 95,
    height: 95,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    qrValue: `https://your-website.com`,
    showLabel: true,
    labelPosition: "bottom",
    label: "Scan to verify invoice"
  });

  // 13. Barcode tracking
  elements.push({
    id: "ind-barcode-track",
    type: "barcode",
    x: 170,
    y: 820,
    width: 280,
    height: 55,
    opacity: 100,
    zIndex: 10,
    rotation: 0,
    barcodeValue: `LEDG-{invoice_number}`
  });

  // 14. Footnotes Terms disclaimer
  elements.push({
    id: "ind-terms-txt",
    type: "text",
    x: 50,
    y: 915,
    width: pageSize === "LETTER" ? 480 : 452,
    height: 80,
    opacity: 100,
    zIndex: 5,
    rotation: 0,
    fontSize: 9,
    fontFamily: "Inter",
    color: "#64748b",
    lineHeight: 1.3,
    textContent: `STANDARD LEDGER NOTICE:\n${specs.taxRate === 0 ? "Tax exempt services. Payment is due within 30 days of issue." : `Standard taxation of ${specs.taxRate}% applied. Payment/settlement due Net 30.`}`
  });

  return {
    name: `${categoryName} Statement ${pageSize === "LETTER" ? "(Letter)" : "(A4)"}`,
    pageSize: pageSize,
    elements: elements,
    category: meta.group
  };
}
export function generateIndianGSTTemplate(pageSize: "A4" | "LETTER" = "A4"): ActiveTemplate {
  const W = pageSize === "LETTER" ? 816 : 794;
  const color = "#1e3a8a";
  const elements: CanvasElement[] = [];

  // ── TOP BORDER BAR ──
  elements.push({ id: "gst-top-bar", type: "shape", x: 0, y: 0, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── OUTER BORDER BOX ──
  elements.push({ id: "gst-outer-box", type: "shape", x: 18, y: 18, width: W - 36, height: 1020, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: "transparent", borderColor: "#1e3a8a", borderWidth: 1.5, borderRadius: 0 });

  // ── TITLE ──
  elements.push({ id: "gst-title", type: "text", x: 18, y: 22, width: W - 36, height: 28, opacity: 100, zIndex: 5, rotation: 0, fontSize: 15, fontWeight: "bold", fontFamily: "Space Grotesk", textAlign: "center", color: "#0f172a", textContent: "Tax Invoice" });

  // ── LINE below title ──
  elements.push({ id: "gst-line1", type: "shape", x: 18, y: 50, width: W - 36, height: 1, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── LOGO ──
  elements.push({ id: "gst-logo-box", type: "shape", x: 22, y: 55, width: 70, height: 55, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: "#e2e8f0", borderColor: "#94a3b8", borderWidth: 1, borderRadius: 4 });
  elements.push({ id: "gst-logo-lbl", type: "text", x: 22, y: 72, width: 70, height: 22, opacity: 100, zIndex: 5, rotation: 0, fontSize: 8, fontFamily: "Inter", textAlign: "center", color: "#64748b", textContent: "LOGO" });

  // ── COMPANY DETAILS ──
  elements.push({ id: "gst-company-name-big", type: "text", x: 100, y: 55, width: 280, height: 22, opacity: 100, zIndex: 6, rotation: 0, fontSize: 13, fontWeight: "bold", fontFamily: "Space Grotesk", color: color, textContent: "TECHMART SOLUTIONS" });
  elements.push({ id: "gst-company", type: "text", x: 100, y: 76, width: 280, height: 110, opacity: 100, zIndex: 5, rotation: 0, fontSize: 10, fontFamily: "Inter", color: "#0f172a", lineHeight: 1.6, textContent: "Ground Floor, Commerce Complex,\nMG Road, Bangalore - 560001\nMob: 9900000001 / 9900000002\nGSTIN/UIN: 29AAACT1234A1Z5\nState Name: Karnataka, Code: 29\nE-Mail: billing@techmartsolutions.in" });

  // ── VERTICAL DIVIDER (company | meta labels) ──
  elements.push({ id: "gst-vdiv1", type: "shape", x: 390, y: 50, width: 1, height: 145, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── INVOICE META LABELS ──
  elements.push({ id: "gst-meta-labels", type: "text", x: 395, y: 55, width: 190, height: 135, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9.5, fontFamily: "Inter", color: "#475569", lineHeight: 2.05, textContent: "Invoice No.\nDated\nDelivery Note\nMode/Terms of Payment\nReference No. & Date\nOther References\nBuyer's Order No.\nDated\nDispatch Doc No.\nDelivery Note Date\nDispatched through\nDestination\nTerms of Delivery" });

  // ── VERTICAL DIVIDER (meta labels | meta values) ──
  elements.push({ id: "gst-vdiv2", type: "shape", x: 590, y: 50, width: 1, height: 145, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── INVOICE META VALUES ──
  elements.push({ id: "gst-meta-values", type: "text", x: 595, y: 55, width: 175, height: 135, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9.5, fontFamily: "JetBrains Mono", color: "#0f172a", lineHeight: 2.05, textContent: "{invoice_number}\n{date}\n\n\n{invoice_number} dt. {date}\n\n\n\n\n\n\n\n" });

  // ── LINE below company/meta block ──
  elements.push({ id: "gst-line2", type: "shape", x: 18, y: 195, width: W - 36, height: 1, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── BUYER DETAILS ──
  elements.push({ id: "gst-buyer-lbl", type: "text", x: 22, y: 200, width: 120, height: 14, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9, fontFamily: "Inter", color: "#475569", textContent: "Buyer (Bill to)" });
  elements.push({ id: "gst-buyer-name", type: "text", x: 22, y: 214, width: 350, height: 55, opacity: 100, zIndex: 5, rotation: 0, fontSize: 11, fontWeight: "bold", fontFamily: "Inter", color: "#0f172a", lineHeight: 1.6, textContent: "{client_details}" });

  // ── LINE below buyer ──
  elements.push({ id: "gst-line3", type: "shape", x: 18, y: 275, width: W - 36, height: 1, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── ITEMS TABLE ──
  const columns: ColumnDefinition[] = [
    { id: "c0", header: "Sl No",                width: 6,  align: "center", key: "slNo" },
    { id: "c1", header: "Description of Goods", width: 30, align: "left",   key: "description" },
    { id: "c2", header: "HSN/SAC",              width: 10, align: "center", key: "hsn" },
    { id: "c3", header: "Quantity",             width: 10, align: "center", key: "qty" },
    { id: "c4", header: "Rate",                 width: 11, align: "right",  key: "rate" },
    { id: "c5", header: "per",                  width: 6,  align: "center", key: "per" },
    { id: "c6", header: "Disc. %",              width: 8,  align: "center", key: "disc" },
    { id: "c7", header: "Amount",               width: 19, align: "right",  key: "total" },
  ];

  const rows: TableRow[] = [
    { id: "r1", values: { slNo: "1", description: "SCREEN FOR LAPTOP 15.6 INCH\n15.6 30PIN IPS LAPCARE 10MONTH WARRANTY", hsn: "8473", qty: 1,  rate: 2881.36, per: "Nos", disc: "",  total: 2881.36 } },
    { id: "r2", values: { slNo: "",  description: "Output CGST @ 9%",                                                      hsn: "",    qty: "", rate: "",       per: "",    disc: "",  total: 259.32  } },
    { id: "r3", values: { slNo: "",  description: "Output SGST @ 9%",                                                      hsn: "",    qty: "", rate: "",       per: "",    disc: "",  total: 259.32  } },
  ];

  elements.push({
    id: "gst-items-table", type: "table",
    x: 18, y: 280, width: W - 36, height: 380,
    opacity: 100, zIndex: 10, rotation: 0,
    color: color, backgroundColor: "#ffffff",
    fontSize: 10, fontFamily: "Inter",
    columns, rows,
    taxRate: 0, discountRate: 0
  });

  // ── TOTAL ROW ──
  elements.push({ id: "gst-line-total", type: "shape", x: 18, y: 660, width: W - 36, height: 1, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  elements.push({ id: "gst-total-lbl",  type: "text",  x: 22,  y: 664, width: 400, height: 20, opacity: 100, zIndex: 5, rotation: 0, fontSize: 10, fontWeight: "bold", fontFamily: "Inter", color: "#0f172a", textContent: "Total" });
  elements.push({ id: "gst-total-qty",  type: "text",  x: 430, y: 664, width: 80,  height: 20, opacity: 100, zIndex: 5, rotation: 0, fontSize: 10, fontFamily: "Inter", textAlign: "center", color: "#0f172a", textContent: "1 Nos." });
  elements.push({ id: "gst-total-amt",  type: "text",  x: 645, y: 664, width: 125, height: 20, opacity: 100, zIndex: 5, rotation: 0, fontSize: 11, fontWeight: "bold", fontFamily: "Space Grotesk", textAlign: "right", color: "#0f172a", textContent: "₹ 3,400.00" });

  // ── LINE below total ──
  elements.push({ id: "gst-line4", type: "shape", x: 18, y: 686, width: W - 36, height: 1, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── AMOUNT IN WORDS ──
  elements.push({ id: "gst-words-lbl", type: "text", x: 22,  y: 690, width: 220, height: 14, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9,  fontFamily: "Inter", color: "#475569", textContent: "Amount Chargeable (in words)" });
  elements.push({ id: "gst-words-val", type: "text", x: 22,  y: 704, width: 500, height: 20, opacity: 100, zIndex: 5, rotation: 0, fontSize: 11, fontWeight: "bold", fontFamily: "Inter", color: "#0f172a", textContent: "INR Three Thousand Four Hundred Only" });
  elements.push({ id: "gst-eoe",       type: "text", x: 600, y: 690, width: 160, height: 14, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9,  fontFamily: "Inter", textAlign: "right", color: "#475569", textContent: "E. & O.E" });

  // ── LINE below words ──
  elements.push({ id: "gst-line5", type: "shape", x: 18, y: 726, width: W - 36, height: 1, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── PAN ──
  elements.push({ id: "gst-pan", type: "text", x: 22, y: 730, width: 300, height: 16, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9.5, fontFamily: "Inter", color: "#0f172a", textContent: "Company's PAN    :   AAACT1234A" });

  // ── DECLARATION ──
  elements.push({ id: "gst-decl-lbl", type: "text", x: 22, y: 750, width: 80,  height: 14,  opacity: 100, zIndex: 5, rotation: 0, fontSize: 9,   fontFamily: "Inter", color: "#475569", textContent: "Declaration" });
  elements.push({ id: "gst-decl-txt", type: "text", x: 22, y: 764, width: 360, height: 110, opacity: 100, zIndex: 5, rotation: 0, fontSize: 8.5, fontFamily: "Inter", color: "#334155", lineHeight: 1.5, textContent: "1. We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.\n2. Interest @ 24% per annum will be charged if the payment is not made on time.\n3. Warranty is on behalf of manufacturers of the products.\n4. Goods once sold will not be taken back.\n5. No warranty on burnt or physical damage, tampered & trackcuts.\n6. Penalty for cheque bounce will be Rs.1000+GST" });

  // ── VERTICAL DIVIDER (decl | bank) ──
  elements.push({ id: "gst-vdiv3", type: "shape", x: 395, y: 726, width: 1, height: 160, opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  // ── BANK DETAILS ──
  elements.push({ id: "gst-bank-lbl",     type: "text", x: 400, y: 730, width: 350, height: 16, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9.5, fontWeight: "bold", fontFamily: "Inter", color: "#0f172a", textContent: "Company's Bank Details" });
  elements.push({ id: "gst-bank-details", type: "text", x: 400, y: 748, width: 350, height: 70, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9.5, fontFamily: "Inter", color: "#0f172a", lineHeight: 1.8, textContent: "Bank Name         :  HDFC BANK\nA/c No.              :  59209900103707\nBranch & IFS Code :  MG ROAD & HDFC0000001" });

  // ── AUTHORISED SIGNATORY ──
  elements.push({ id: "gst-for-co",   type: "text",      x: 545, y: 820, width: 220, height: 16, opacity: 100, zIndex: 5,  rotation: 0, fontSize: 9.5, fontFamily: "Inter", textAlign: "right", color: "#0f172a", textContent: "for TECHMART SOLUTIONS" });
  elements.push({ id: "gst-auth-sig", type: "signature", x: 560, y: 838, width: 180, height: 50, opacity: 100, zIndex: 10, rotation: 0, signatureName: "{staff_name}", textContent: "Authorised Signatory" });

  // ── BOTTOM SECTION ──
  elements.push({ id: "gst-line6",       type: "shape", x: 18, y: 886, width: W - 36, height: 1,  opacity: 100, zIndex: 2, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });
  elements.push({ id: "gst-jurisdiction", type: "text", x: 18, y: 890, width: W - 36, height: 16, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9.5, fontFamily: "Inter", textAlign: "center", color: "#0f172a", textContent: "SUBJECT TO BANGALORE JURISDICTION" });
  elements.push({ id: "gst-computer-gen", type: "text", x: 18, y: 906, width: W - 36, height: 16, opacity: 100, zIndex: 5, rotation: 0, fontSize: 9,   fontFamily: "Inter", textAlign: "center", color: "#64748b", textContent: "This is a Computer Generated Invoice" });

  // ── BOTTOM BORDER BAR ──
  elements.push({ id: "gst-bottom-bar", type: "shape", x: 0, y: 1044, width: W, height: 6, opacity: 100, zIndex: 1, rotation: 0, backgroundColor: color, borderWidth: 0, borderRadius: 0 });

  return {
    name: `Indian GST Tax Invoice - TECHMART SOLUTIONS (${pageSize})`,
    pageSize: pageSize,
    elements,
    category: "Retail & Commerce"
  };
}
